import React, { useMemo, useRef, useState } from 'react';
import { Client, Flight, Hotel, Place, TripInfo } from '../../types/Quotation';
import { QuotationRoomOccupancy, summarizeQuotationRooms, toTravellerInfoFromRooms } from './quotationRoomOccupancy';
import FlightListModal, { FlightSearchContext } from './FlightListModal';
import HotelListModal, { HotelSearchContext } from './HotelListModal';
import StandaloneQuotationPDF from './StandaloneQuotationPDF';
import ShareQuotationButton from '../PackageBuilder/ShareQuotationButton';

type StandaloneQuotationWorkflowProps = {
  kind: 'flight' | 'hotel';
  review: boolean;
  sourceQueryId?: number;
  client: Client;
  customerId?: number;
  assignedId?: number;
  createdBy?: number;
  hotelRooms: QuotationRoomOccupancy[];
  onHotelRoomsChange: (rooms: QuotationRoomOccupancy[]) => void;
  defaultStartDate: string;
  defaultPlace?: Place;
  role?: string;
  onProceed: () => void;
};

const DEFAULT_MARKUP = 15;
const MIN_MARKUP = 12;
const GST_RATE = 0.05;
const money = (amount: number) => `₹ ${Number(amount || 0).toLocaleString('en-IN')}`;
const dateDiff = (from: string, to: string) => {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, Math.round((end - start) / 86400000)) : 0;
};

const StandaloneQuotationWorkflow: React.FC<StandaloneQuotationWorkflowProps> = ({
  kind, review, sourceQueryId, client, customerId, assignedId, createdBy, hotelRooms, onHotelRoomsChange,
  defaultStartDate, defaultPlace, role, onProceed,
}) => {
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [flightContext, setFlightContext] = useState<FlightSearchContext>({ departureDate: defaultStartDate, isRoundTrip: false });
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [hotelContext, setHotelContext] = useState<HotelSearchContext | null>(null);
  const [markup, setMarkup] = useState(DEFAULT_MARKUP);

  const travellers = useMemo(() => toTravellerInfoFromRooms(hotelRooms), [hotelRooms]);
  const supplierTotal = kind === 'flight'
    ? flights.reduce((sum, flight) => sum + Number(flight.price || 0), 0)
    : Number(hotel?.display_price ?? hotel?.price ?? 0);
  const markupAmount = Math.round(supplierTotal * (markup / 100));
  const totalPrice = Math.round((supplierTotal + markupAmount) * (1 + GST_RATE));
  const startDate = kind === 'flight' ? flightContext.departureDate : (hotelContext?.checkIn || defaultStartDate);
  const endDate = kind === 'flight'
    ? (flightContext.returnDate || flightContext.departureDate)
    : (hotelContext?.checkOut || startDate);
  const nightsCount = dateDiff(startDate, endDate);
  const selected = kind === 'flight' ? flights.length > 0 : Boolean(hotel);
  const title = kind === 'flight'
    ? (flights.length ? `Flight quotation: ${flights[0].from} to ${flights[0].to}` : 'Flight Quotation')
    : (hotel ? `Hotel quotation: ${hotel.name}` : 'Hotel Quotation');
  const tripInfo = useMemo<TripInfo>(() => ({
    title,
    startDate,
    places: kind === 'hotel' && hotelContext ? [{ label: hotelContext.city.label, value: hotelContext.city.value, nights: nightsCount }] : [],
    travellers,
    inclusions: [kind === 'flight' ? 'flights' : 'hotel'],
    totalPrice,
  }), [hotelContext, kind, nightsCount, startDate, title, totalPrice, travellers]);
  const providerRef = useMemo(() => ({
    quotation: { kind, origin: 'standalone' },
    tripInfo,
    travelStartDate: startDate,
    travelEndDate: endDate,
    ...(kind === 'flight' ? { flights } : {
      hotels: hotel ? [{
        ...hotel,
        city: hotelContext?.city.label || hotel.city_name || '',
        city_name: hotelContext?.city.label || hotel.city_name || '',
        city_code: hotelContext?.city.value || hotel.city_code || '',
        checkInDate: startDate,
        checkOutDate: endDate,
      }] : [],
    }),
  }), [endDate, flights, hotel, hotelContext, kind, startDate, tripInfo]);

  const selectFlight = (flight: Flight, leg: 'outbound' | 'return') => {
    setFlights((current) => leg === 'outbound' ? [flight] : [current[0], flight].filter(Boolean));
  };

  return (
    <div className="quotation-workflow quotation-workflow--standalone">
      <section className="quotation-workflow-card">
        <div className="quotation-workflow-card__header"><div className="quotation-workflow-card__step">1</div><div><h2>{kind === 'flight' ? 'Select Flights' : 'Select Hotel'}</h2><p>Search and select the service to quote.</p></div></div>
        {kind === 'flight' ? (
          <FlightListModal
            embedded
            startDate={defaultStartDate}
            travellers={travellers}
            rooms={hotelRooms}
            onRoomsChange={onHotelRoomsChange}
            onClose={() => undefined}
            onSelectOutbound={(flight) => selectFlight(flight, 'outbound')}
            onSelectReturn={(flight) => selectFlight(flight, 'return')}
            onSelectionContext={setFlightContext}
          />
        ) : (
          <HotelListModal
            embedded
            place={defaultPlace || { label: '', value: '', nights: 0 }}
            checkIn={defaultStartDate}
            checkOut={defaultStartDate}
            travellers={travellers}
            rooms={hotelRooms}
            onRoomsChange={onHotelRoomsChange}
            onClose={() => undefined}
            onSelect={setHotel}
            onSelectionContext={setHotelContext}
          />
        )}
      </section>

      <section className="quotation-workflow-card">
        <div className="quotation-workflow-card__header"><div className="quotation-workflow-card__step">2</div><div><h2>Review & Confirm</h2><p>Confirm selected service and quotation pricing.</p></div></div>
        {!selected ? <div className="quotation-empty-state">Select {kind === 'flight' ? 'a flight' : 'a hotel'} to continue.</div> : (
          <>
            <div className="quotation-review-list">
              <div className="quotation-review-row"><span>Service</span><strong>{title}</strong></div>
              <div className="quotation-review-row"><span>Travel dates</span><strong>{startDate}{endDate && endDate !== startDate ? ` - ${endDate}` : ''}</strong></div>
              <div className="quotation-review-row"><span>Rooms & Travellers</span><strong>{summarizeQuotationRooms(hotelRooms)}</strong></div>
            </div>
            <div className="quotation-field quotation-field--full" style={{ marginTop: 16 }}>
              <label>Markup (%)</label>
              <input type="number" min={MIN_MARKUP} step="1" value={markup} onChange={(event) => setMarkup(Math.max(MIN_MARKUP, Number(event.target.value || DEFAULT_MARKUP)))} />
              <div className="quotation-empty-state" style={{ marginTop: 8 }}>Default markup is {DEFAULT_MARKUP}%. Minimum allowed markup is {MIN_MARKUP}%. GST at 5% is applied after markup.</div>
            </div>
            <div className="quotation-summary-panels">
              <div className="quotation-summary-panel"><span>Supplier price</span><strong>{money(supplierTotal)}</strong></div>
              <div className="quotation-summary-panel"><span>Markup</span><strong>{money(markupAmount)}</strong></div>
            </div>
            <div className="quotation-total-card"><span>Total quotation price (incl. GST)</span><strong>{money(totalPrice)}</strong></div>
            {!review && <div className="quotation-section-actions"><button type="button" className="quotation-primary-btn" onClick={onProceed}>Proceed to PDF Preview</button></div>}
          </>
        )}
      </section>

      {review && selected && (
        <section className="quotation-workflow-card">
          <div className="quotation-workflow-card__header"><div className="quotation-workflow-card__step">3</div><div><h2>Quotation Preview (PDF)</h2><p>Download and share the final quotation.</p></div></div>
          {role !== 'sales' && <div className="quotation-preview-actions"><ShareQuotationButton productType={kind} nightsCount={nightsCount} queryId={sourceQueryId} startDate={startDate} hotelRooms={hotelRooms} totalPrice={totalPrice} providerRef={providerRef} pdfRef={pdfRef} currency="INR" title={title} client={client} customerId={customerId} assignedId={assignedId} createdBy={createdBy} label="Download & Share" download shareDraft className="quotation-primary-btn" /></div>}
          {!sourceQueryId && <div className="quotation-warning quotation-warning--soft">This quotation is not attached to a query yet. The PDF will download, but cannot be shared until attached to a query.</div>}
          <div className="quotation-pdf-shell"><div ref={pdfRef}><StandaloneQuotationPDF kind={kind} client={client} tripInfo={tripInfo} flights={flights} hotel={hotel} checkIn={startDate} checkOut={endDate} selectedRoomCount={hotelRooms.length} /></div></div>
        </section>
      )}
    </div>
  );
};

export default StandaloneQuotationWorkflow;
